import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { customToast } from '../utils/toastUtils';
import { db, auth, storage } from '../firebase/firebaseConfig';
import { collection, getDocs, query, orderBy, deleteDoc, doc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { ref, deleteObject } from 'firebase/storage';
import { onAuthStateChanged } from 'firebase/auth';
import LimitExceededModal from '../components/LimitExceededModal';
import useAddonPurchase from '../hooks/useAddonPurchase';
import { generateFilename } from '../utils/pdfExporter';
import './MyFiles.css';

// SVG Icons as components
const FileIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" className="file-type-icon">
    <path 
      d="M13 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V9L13 2Z" 
      fill="#6366F1"
      stroke="#4F46E5" 
      strokeWidth="2" 
      strokeLinecap="round" 
      strokeLinejoin="round"
    />
    <path d="M13 2V9H20" stroke="#E0E7FF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <line x1="8" y1="13" x2="16" y2="13" stroke="#E0E7FF" strokeWidth="1.5" strokeLinecap="round"/>
    <line x1="8" y1="16" x2="14" y2="16" stroke="#E0E7FF" strokeWidth="1.5" strokeLinecap="round"/>
  </svg>
);

const PdfIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" className="format-icon-svg">
    <path 
      d="M13 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V9L13 2Z" 
      fill="#DC2626"
      stroke="#B91C1C" 
      strokeWidth="2" 
      strokeLinecap="round" 
      strokeLinejoin="round"
    />
    <path d="M13 2V9H20" stroke="#FEE2E2" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <text x="12" y="17" textAnchor="middle" fill="white" fontSize="5" fontWeight="bold">PDF</text>
  </svg>
);

const DocxIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" className="format-icon-svg">
    <path 
      d="M13 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V9L13 2Z" 
      fill="#2563EB"
      stroke="#1E40AF" 
      strokeWidth="2" 
      strokeLinecap="round" 
      strokeLinejoin="round"
    />
    <path d="M13 2V9H20" stroke="#DBEAFE" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <text x="12" y="17.5" textAnchor="middle" fill="white" fontSize="4" fontWeight="bold">DOCX</text>
  </svg>
);

const EditIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="action-icon">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
    <path d="M18.5 2.5a2.121 2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
  </svg>
);

const DownloadIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="action-icon">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
    <polyline points="7 10 12 15 17 10"/>
    <line x1="12" y1="15" x2="12" y2="3"/>
  </svg>
);

const DeleteIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="action-icon">
    <polyline points="3 6 5 6 21 6"/>
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
    <line x1="10" y1="11" x2="10" y2="17"/>
    <line x1="14" y1="11" x2="14" y2="17"/>
  </svg>
);

const CalendarIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="meta-icon">
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
    <line x1="16" y1="2" x2="16" y2="6"/>
    <line x1="8" y1="2" x2="8" y2="6"/>
    <line x1="3" y1="10" x2="21" y2="10"/>
  </svg>
);

const DatabaseIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="meta-icon">
    <ellipse cx="12" cy="5" rx="9" ry="3"/>
    <path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3"/>
    <path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5"/>
  </svg>
);

const FolderIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="header-icon">
    <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/>
  </svg>
);

const EmptyIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="empty-icon">
    <path d="M13 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z"/>
    <polyline points="13 2 13 9 20 9"/>
  </svg>
);

const MyFiles = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(null);
  const [downloading, setDownloading] = useState(null);
  const [editing, setEditing] = useState(null);
  const [showFormatMenu, setShowFormatMenu] = useState(null);
  const [user, setUser] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);
  const [showAddonModal, setShowAddonModal] = useState(false);
  const [limitModalFile, setLimitModalFile] = useState(null); // file that triggered the limit
  const { purchaseAddon } = useAddonPurchase();

  // Show post-payment success toast when redirected from pricing
  useEffect(() => {
    if (location.state?.showSuccess && location.state?.message) {
      customToast.success(location.state.message);
    }
  }, []);

  useEffect(() => {
    // Listen for auth state changes
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
      setAuthChecked(true);
      
      if (currentUser) {
        loadFiles(currentUser);
      } else {
        setLoading(false);
        // Redirect to login or profile after a moment
        setTimeout(() => {
          alert('Please sign in to view your files');
          navigate('/profile');
        }, 500);
      }
    });

    return () => unsubscribe();
  }, [navigate]);

  // Close dropdown when clicking outside any download-wrapper
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!e.target.closest('.download-wrapper')) {
        setShowFormatMenu(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadFiles = async (currentUser) => {
    try {
      if (!currentUser) {
        return;
      }

      setLoading(true);

      // Get resumes from Firestore (these contain the editable data)
      const resumesRef = collection(db, 'users', currentUser.uid, 'resumes');
      const q = query(resumesRef, orderBy('updatedAt', 'desc'));
      const querySnapshot = await getDocs(q);

      const filesList = [];
      querySnapshot.forEach((docSnapshot) => {
        const data = docSnapshot.data();
        filesList.push({
          id: docSnapshot.id,
          resumeId: docSnapshot.id,
          ...data
        });
      });

      setFiles(filesList);

    } catch (error) {
      console.error('❌ Error loading resumes:', error);
      alert('Failed to load resumes. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (file, format) => {
    try {
      if (!file.htmlDownloadURL) {
        alert('HTML file not found. Cannot generate download.');
        return;
      }

      // Check if user has an active paid plan before allowing download
      const planCheckRef = doc(db, 'users', user.uid);
      const planCheckSnap = await getDoc(planCheckRef);
      const userPlanData = planCheckSnap.data() || {};

      const planType = userPlanData.planType || 'free';
      const isActivePlan = userPlanData.isActivePlan || false;

      if (planType === 'free' || !isActivePlan) {
        customToast.error('Your plan has expired. Please upgrade to download your resume.');
        navigate('/pricing', {
          state: {
            message: 'Upgrade your plan to download resumes from My Files.',
            returnUrl: '/my-files'
          }
        });
        return;
      }

      // Check if download limit has been reached
      const resumeDownloadsUsed = userPlanData.resumeDownloadsUsed || 0;
      const resumeDownloadLimit = userPlanData.resumeDownloadLimit;
      const isUnlimited = resumeDownloadLimit === -1 || resumeDownloadLimit === 'unlimited';

      if (!isUnlimited && resumeDownloadsUsed >= resumeDownloadLimit) {
        // Show the addon/limit modal instead of a toast
        setLimitModalFile(file);
        setShowAddonModal(true);
        return;
      }

      setDownloading(file.id);
      setShowFormatMenu(null);

      // Fetch the HTML content from Storage
      const response = await fetch(file.htmlDownloadURL);
      const htmlContent = await response.text();
      
      // Send to backend for PDF/DOCX generation
      const backendUrl = import.meta.env.VITE_BACKEND_URL || 'http://localhost:5000';
      
      // Generate proper filename from resume data
      const downloadName = file.data
        ? generateFilename(file.data).replace(/\.pdf$/i, `.${format}`)
        : `${file.resumeName}.${format}`;

      // For DOCX, include structured resume data for editable text generation
      const requestBody = {
        html: htmlContent,
        fileName: downloadName,
        userId: user.uid
      };
      
      if (format === 'docx' && file.data) {
        requestBody.resumeData = file.data;
        requestBody.fontFamily = file.selectedFont || 'Calibri';
      }
      
      const exportResponse = await fetch(`${backendUrl}/api/export/${format}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });

      if (!exportResponse.ok) {
        throw new Error(`Export failed: ${exportResponse.statusText}`);
      }

      // Get the file blob
      const blob = await exportResponse.blob();
      
      // Trigger download
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = downloadName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      // Increment resumeDownloadsUsed counter
      const userRef = doc(db, 'users', user.uid);
      const userSnap = await getDoc(userRef);
      const userData = userSnap.data() || {};
      
      await updateDoc(userRef, {
        resumeDownloadsUsed: (userData.resumeDownloadsUsed || 0) + 1,
        lastDownloadAt: serverTimestamp(),
        [`${format}DownloadCount`]: (userData[`${format}DownloadCount`] || 0) + 1
      });

      alert(`✅ ${format.toUpperCase()} downloaded successfully!`);

    } catch (error) {
      console.error('❌ Error downloading file:', error);
      alert(`Failed to download: ${error.message}`);
    } finally {
      setDownloading(null);
    }
  };

  const handleEdit = async (file) => {
    try {
      if (!file.resumeId) {
        alert('Cannot edit this resume. Resume ID is missing.');
        return;
      }

      // Show loading state
      setEditing(file.id);
      
      // Fetch the resume document from Firestore
      const resumeRef = doc(db, 'users', user.uid, 'resumes', file.resumeId);
      const resumeSnap = await getDoc(resumeRef);
      
      if (!resumeSnap.exists()) {
        alert('❌ Resume not found. It may have been deleted.');
        setDownloading(null);
        return;
      }
      
      const resumeData = resumeSnap.data();
      
      // Validate that we have resume data
      if (!resumeData.data) {
        alert('⚠️ Resume data is empty or corrupted. Cannot edit this resume.');
        setDownloading(null);
        return;
      }
      
      // Navigate to V2 editor with selectedTemplate object + editing data
      // IMPORTANT: editTemplateHTML must be the RAW mustache template (resumeData.templateHTML),
      // not the rendered HTML from htmlDownloadURL. The rendered HTML has data baked in and
      // cannot be re-rendered with new edits.
      navigate('/resume-editor-v2', {
        state: {
          selectedTemplate: {
            id: resumeData.templateId || file.templateId || null,
            name: resumeData.templateName || file.templateName || 'Unknown',
            tier: resumeData.templateTier || '',
            plan: resumeData.templateTier || '',
            downloadURL: file.htmlDownloadURL || null
          },
          // Editing-specific state
          isEditing: true,
          editResumeId: file.resumeId,
          editResumeData: resumeData.data,
          editTemplateHTML: resumeData.templateHTML || null, // RAW template with {{placeholders}}
          editColorTheme: resumeData.templateColorTheme || '',
          editFont: resumeData.selectedFont || 'Calibri',
          editHiddenSections: resumeData.hiddenSections || []
        }
      });
      
    } catch (error) {
      console.error('❌ Error loading resume for editing:', error);
      alert(`Failed to load resume: ${error.message}`);
    } finally {
      setEditing(null); // Clear loading state
    }
  };

  const handleDelete = async (file) => {
    if (!window.confirm(`Delete "${file.resumeName || 'this resume'}"?\n\nThis will delete the resume and all associated files. This action cannot be undone.`)) {
      return;
    }

    try {
      setDeleting(file.id);

      // Delete HTML file from Storage if exists
      if (file.htmlStoragePath) {
        try {
          const htmlStorageRef = ref(storage, file.htmlStoragePath);
          await deleteObject(htmlStorageRef);
        } catch (storageError) {
          // Storage file may already be deleted
        }
      }

      // Delete PDF file from Storage if exists
      if (file.lastPdfStoragePath) {
        try {
          const pdfStorageRef = ref(storage, file.lastPdfStoragePath);
          await deleteObject(pdfStorageRef);
        } catch (storageError) {
          // Storage file may already be deleted
        }
      }

      // Delete DOCX file from Storage if exists
      if (file.lastDocxStoragePath) {
        try {
          const docxStorageRef = ref(storage, file.lastDocxStoragePath);
          await deleteObject(docxStorageRef);
        } catch (storageError) {
          // Storage file may already be deleted
        }
      }

      // Delete resume document from Firestore
      const docRef = doc(db, 'users', user.uid, 'resumes', file.id);
      await deleteDoc(docRef);

      // Remove from local state
      setFiles(files.filter(f => f.id !== file.id));
      
      alert('✅ Resume deleted successfully');

    } catch (error) {
      console.error('❌ Error deleting resume:', error);
      alert('Failed to delete resume. Please try again.');
    } finally {
      setDeleting(null);
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes || bytes === 0) return 'N/A';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const formatTotalSize = () => {
    const totalBytes = files.reduce((sum, file) => sum + (file.lastFileSize || 0), 0);
    if (totalBytes === 0) return 'N/A';
    return formatFileSize(totalBytes);
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'Not downloaded yet';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString();
  };

  if (loading) {
    return (
      <div className="my-files-container">
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading your files...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="my-files-container">
      <div className="my-files-header">
        <div className="header-title">
          <FolderIcon />
          <h1>My Files</h1>
        </div>
        <p>All your downloaded resumes are stored here. Edit, download, or manage your files.</p>
        <div className="files-expiry-note">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{flexShrink:0, marginTop:'1px'}}>
            <circle cx="12" cy="12" r="10"/>
            <line x1="12" y1="8" x2="12" y2="12"/>
            <line x1="12" y1="16" x2="12.01" y2="16"/>
          </svg>
          <span>All your saved resumes will be removed once your plan expires.</span>
        </div>
      </div>

      {files.length === 0 ? (
        <div className="empty-state">
          <EmptyIcon />
          <h2>No files yet</h2>
          <p>Download a resume from the editor to see it here</p>
          <button className="btn-primary" onClick={() => navigate('/templates')}>
            Create Resume
          </button>
        </div>
      ) : (
        <>
          <div className="files-grid">
            {files.map((file) => (
              <div key={file.id} className="file-card">
                <div className="file-header">
                  <div className="file-icon-wrapper">
                    <FileIcon />
                  </div>
                  <div className="file-main-info">
                    <h3 className="file-name" title={file.resumeName || 'Untitled Resume'}>
                      {file.resumeName || 'Untitled Resume'}
                    </h3>
                  </div>
                </div>

                <div className="file-meta">
                  <span className="meta-item">
                    <CalendarIcon />
                    <span>{formatDate(file.updatedAt)}</span>
                  </span>
                  <span className="meta-item">
                    <DatabaseIcon />
                    <span>{formatFileSize(file.lastFileSize)}</span>
                  </span>
                </div>

                <div className="file-actions">
                  <button
                    className="btn-action btn-edit"
                    onClick={() => handleEdit(file)}
                    disabled={editing === file.id}
                    title="Edit resume in editor"
                  >
                    {editing === file.id ? (
                      <span className="spinner-small"></span>
                    ) : (
                      <EditIcon />
                    )}
                    <span>Edit</span>
                  </button>
                  
                  <div 
                    className="download-wrapper"
                  >
                    <button
                      className="btn-action btn-download"
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowFormatMenu(showFormatMenu === file.id ? null : file.id);
                      }}
                      disabled={downloading === file.id || !file.htmlDownloadURL}
                      title={file.htmlDownloadURL ? "Download resume" : "No file available"}
                    >
                      {downloading === file.id ? (
                        <span className="spinner-small"></span>
                      ) : (
                        <>
                          <span>Download</span>
                          <svg className="dropdown-arrow" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                            <polyline points="6 9 12 15 18 9"></polyline>
                          </svg>
                        </>
                      )}
                    </button>
                    
                    {showFormatMenu === file.id && (
                      <div className="format-dropdown">
                        <button 
                          className="format-option"
                          onClick={() => handleDownload(file, 'pdf')}
                        >
                          <div className="format-icon-wrapper">
                            <PdfIcon />
                          </div>
                          <div className="format-text">
                            <span className="format-title">.pdf</span>
                            <span className="format-desc">Portable Document Format</span>
                          </div>
                        </button>
                        <button 
                          className="format-option"
                          onClick={() => handleDownload(file, 'docx')}
                        >
                          <div className="format-icon-wrapper">
                            <DocxIcon />
                          </div>
                          <div className="format-text">
                            <span className="format-title">.docx</span>
                            <span className="format-desc">Microsoft Word Format</span>
                          </div>
                        </button>
                      </div>
                    )}
                  </div>
                  
                  <button
                    className="btn-action btn-delete"
                    onClick={() => handleDelete(file)}
                    disabled={deleting === file.id}
                    title="Delete resume"
                  >
                    {deleting === file.id ? (
                      <span className="spinner-small"></span>
                    ) : (
                      <DeleteIcon />
                    )}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      <div className="files-footer">
        <button className="btn-secondary" onClick={() => navigate('/profile')}>
          Back to Profile
        </button>
      </div>

      {/* Limit Exceeded Modal - Add-On Purchase */}
      <LimitExceededModal
        isOpen={showAddonModal}
        onClose={() => { setShowAddonModal(false); setLimitModalFile(null); }}
        limitType="download"
        templateTier={limitModalFile?.templateTier || ''}
        resumeState={limitModalFile ? { resumeData: limitModalFile.data } : null}
        onBuyAddon={async (addonData) => {
          const success = await purchaseAddon({
            addonData,
            limitType: 'download',
            templateId: limitModalFile?.templateId || '',
            templateTier: limitModalFile?.templateTier || '',
          });
          if (success) {
            setShowAddonModal(false);
            // Retry download after successful addon purchase
            if (limitModalFile) {
              setTimeout(() => handleDownload(limitModalFile, 'pdf'), 500);
            }
            setLimitModalFile(null);
          }
        }}
      />
    </div>
  );
};

export default MyFiles;
